/**
 * Manufacturing bucket surface tokens (palette-pass P1).
 *
 * Re-exports canonical strings from `surfaceClasses.ts` and adds sizing
 * variants used across inventory, plants, models, and receipts UI.
 * Prefer imports from this module over local `const …Class` duplicates.
 *
 * Dark neutrals use `surface-*` CSS-var colors (Special flips via
 * `src/surfaceTokens.css` — special dark mode Slice 3).
 */

export {
  cardSurfaceClass,
  sectionCardClass,
  inputClass,
  ghostBtnClass,
  secondaryBtnClass,
  compactInputClass,
  insetSurfaceCardClass,
  secondarySurfaceClass,
  cardTitleTextClass,
  cardMutedTextClass,
  cardDividerBorderClass,
  cardHoverSurfaceClass,
  adminDangerGhostBtnClass,
} from "@/components/common/surfaceClasses";

/** Standard indigo primary CTA (`px-3 py-1.5`). */
export const mfgPrimaryBtnClass =
  "rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";

/** Teal accent CTA — inventory count and other find-me actions. */
export const mfgAccentBtnClass =
  "rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60";

/** Modal / form primary CTA (`px-4 py-2`). */
export const mfgPrimaryBtnLgClass =
  "rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";

/** Compact primary for SectionCard `rightSlot` toolbars. */
export const mfgCompactPrimaryBtnClass =
  "rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";

/** Outline cancel / secondary (`px-4 py-2`). */
export const mfgSecondaryBtnClass =
  "rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** Compact outline (`px-2.5 py-1 text-xs`). */
export const mfgCompactGhostBtnClass =
  "rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** xs outline in receipt / changelog toolbars (`rounded-md`). */
export const mfgXsGhostBtnClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** xs secondary with font-medium (`rounded-md`). */
export const mfgXsSecondaryBtnClass =
  "rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** Filter-bar ghost (`text-xs` on standard padding). */
export const mfgFilterGhostBtnClass =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** Receive-lines submit affordance. */
export const mfgGhostBtnSemiboldClass =
  "shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** Inline field without `w-full` (BOM / attachments rows). */
export const mfgInlineInputClass =
  "rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** xs filter / plant-location inputs (`px-2 py-1`). */
export const mfgXsInputClass =
  "rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** xs filter inputs with slightly taller hit target (`py-1.5`). */
export const mfgXsInputTallClass =
  "rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** Dictation / combobox fields (`rounded-md border-gray-300`). */
export const mfgMdInputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** Micro inline editor (`text-[11px]`). */
export const mfgMicroInputClass =
  "rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** Vendor autocomplete with disabled-state styling. */
export const mfgDisabledInputClass =
  "rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:disabled:bg-surface-hover dark:disabled:text-surface-ink-muted";

/** xs indigo primary (`rounded-md`). */
export const mfgXsPrimaryBtnClass =
  "rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";

/** xs indigo primary with standard text size. */
export const mfgMdPrimaryBtnClass =
  "rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";

/** Dictation fields (`rounded-md`, no `w-full`). */
export const mfgMdSelectClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** Modal shell (add `max-w-*` / `p-*` at call site). */
export const mfgModalShellClass =
  "rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-surface-border dark:bg-surface-canvas";

/** Solid red destructive CTA (hard-delete confirms). */
export const mfgSolidDangerBtnClass =
  "rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60";

/** Confirm-dialog card base (add `max-w-*` at call site). */
export const mfgConfirmDialogClass =
  "w-full space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-lg dark:border-surface-border dark:bg-surface-canvas";

/** Dashed empty-state plate inside cards. */
export const mfgEmptyStateClass =
  "rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-surface-border dark:bg-surface-canvas dark:text-surface-ink-muted";
